import axios from "axios";
import { readToken, readUser } from "utils/session";

function apiBase() {
  return process.env.REACT_APP_API_BASE_URL || "";
}

function authHeaders() {
  const token = readToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function failMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback;
}

export async function fetchOrgTree() {
  try {
    const response = await axios.get(`${apiBase()}/api/org/tree`, { headers: authHeaders() });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "Could not load the organization."));
  }
}

function normalizePlaces(data) {
  const branches = data.branches || [];
  let zones = data.zones || [];
  if (!zones.length) {
    const seen = new Map();
    branches.forEach((branch) => {
      if (branch.zoneId == null || seen.has(branch.zoneId)) return;
      seen.set(branch.zoneId, { zoneId: branch.zoneId, name: branch.zoneName || "" });
    });
    zones = [...seen.values()];
  }
  let customers = data.customers || [];
  if (!customers.length) {
    const user = readUser();
    const customerId = user?.access?.customerId;
    if (customerId) {
      customers = [{ customerId, name: user.customerName || "This customer" }];
    }
  }
  return { customerId: data.customerId, zones, branches, customers };
}

async function getDevices(params, fallback) {
  try {
    const response = await axios.get(`${apiBase()}/api/org/devices`, {
      headers: authHeaders(),
      params,
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, fallback));
  }
}

export async function fetchOrgPlaces() {
  const data = await getDevices({ section: "places" }, "Could not load zones and branches.");
  return normalizePlaces(data);
}

export async function fetchSurplusDevices() {
  const data = await getDevices({ section: "surplus" }, "Could not load surplus devices.");
  const places = data.zones || data.branches ? normalizePlaces(data) : null;
  return { surplus: data.surplus || [], places };
}

export async function fetchAssignedDevice(did) {
  const data = await getDevices(
    { section: "assigned", did },
    "Could not load the assigned device."
  );
  const rows = data.assigned || [];
  return rows.filter((row) => String(row.did) === String(did));
}

export async function fetchAssignmentHistory({ from, to, customerId }) {
  const data = await getDevices(
    { section: "history", from, to, customerId },
    "Could not load assignment history."
  );
  return (data.history || []).filter((row) => {
    const day = String(row.createdAt || "").slice(0, 10);
    return day >= from && day <= to;
  });
}

export async function assignDevice(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/org/devices/assign`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The device was not assigned."));
  }
}

export async function createZone(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/org/zones`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The zone was not created."));
  }
}

export async function createBranch(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/org/branches`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The branch was not created."));
  }
}

export async function deleteZone(zoneId) {
  try {
    const response = await axios.delete(`${apiBase()}/api/org/zones/${zoneId}`, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The zone was not deleted."));
  }
}

export async function deleteBranch(branchId) {
  try {
    const response = await axios.delete(`${apiBase()}/api/org/branches/${branchId}`, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The branch was not deleted."));
  }
}

export async function renameDeviceAsset(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/org/devices/asset`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The asset id was not saved."));
  }
}

export async function fetchAssetHistory({ from, to, customerId }) {
  const data = await getDevices(
    { section: "assethistory", from, to, customerId },
    "Could not load asset id history."
  );
  return (data.history || []).filter((row) => {
    const day = String(row.createdAt || "").slice(0, 10);
    return day >= from && day <= to;
  });
}

export async function changeOwnPassword(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/account/password`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The password was not changed."));
  }
}

export async function setLoginPassword(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/org/users/password`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The password was not saved."));
  }
}

export async function recallDevice(payload) {
  try {
    const response = await axios.post(`${apiBase()}/api/org/devices/recall`, payload, {
      headers: { ...authHeaders(), "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error) {
    throw new Error(failMessage(error, "The device was not recalled."));
  }
}
